import React from 'react'
import './richEditor.scss';
import { Editor, EditorState, RichUtils, convertToRaw, convertFromRaw, CompositeDecorator, Modifier } from 'draft-js';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import 'draft-js/dist/Draft.css';
import { TextBold16Filled, TextItalic16Filled, TextBulletListLtr16Regular, TextNumberListLtr16Regular, Link16Regular } from '@fluentui/react-icons'
import { Popover, PopoverTrigger, PopoverSurface, Field, Button, Input, OnOpenChangeData, InputOnChangeData, Link, Tooltip } from '@fluentui/react-components'


interface IMyEditorProps {
    onPropertyChanged: (value: string) => void;
    placeholder?: string;
    propertyType: string;
    initialValue?: string;
    draftIsMount: boolean;
}

interface IMyEditorState {
    editorState: EditorState;
    initialValue: string;
    propertyType: string;
    draftIsMount: boolean;
    onPropertyChanged: (value: any) => void;
    isLinkPopoverOpen: boolean;
    linkText: string;
    linkAddress: string;
    linkAddressErrorMessage: string;
}

const InlineLink = (props: any) => {
    const { url } = props.contentState.getEntity(props.entityKey).getData();
    return (
        <Link href={url}>
            {props.children}
        </Link>
    );
};

class MyEditor extends React.Component<IMyEditorProps, IMyEditorState> {
    onChange: (editorState: any) => void;
    constructor(props: IMyEditorProps) {
        super(props);
        const markdownString = props.initialValue;
        const rawData = markdownToDraft(markdownString || "");
        const contentState = convertFromRaw(rawData);

        const decorator = new CompositeDecorator([
            {
                strategy: this.findLinkEntities,
                component: InlineLink,
            },
        ]);

        this.state = {
            editorState: EditorState.createWithContent(contentState, decorator),
            initialValue: props.initialValue || "",
            draftIsMount: props.draftIsMount,
            onPropertyChanged: props.onPropertyChanged,
            propertyType: props.propertyType,
            isLinkPopoverOpen: false,
            linkText: "",
            linkAddress: "",
            linkAddressErrorMessage: ""
        };
        this.onChange = editorState => { this.setState({ editorState }); props.onPropertyChanged(this.toMD(editorState)); };
        this.handleKeyCommand = this.handleKeyCommand.bind(this);
        this.toggleInlineStyle = this.toggleInlineStyle.bind(this);
        this.toggleBlock = this.toggleBlock.bind(this);
        this.toMD = this.toMD.bind(this);
        this.getLinkPrompt = this.getLinkPrompt.bind(this);
        this.cancelAddingLink = this.cancelAddingLink.bind(this);
        this.handleOpenChange = this.handleOpenChange.bind(this);
        this.handleLinkAddressChange = this.handleLinkAddressChange.bind(this);
        this.getIsLinkTriggerDisabled = this.getIsLinkTriggerDisabled.bind(this);
    }

    static getDerivedStateFromProps(newProps: any, state: any) {
        if (newProps.draftIsMount != state.draftIsMount) {
            const markdownString = newProps.initialValue;
            const rawData = markdownToDraft(markdownString || "");
            const contentState = convertFromRaw(rawData);

            return { editorState: EditorState.createWithContent(contentState), draftIsMount: true }
        } else {
            return null
        }
    }

    findLinkEntities(contentBlock: Draft.DraftModel.ImmutableData.ContentBlock, callback: (start: number, end: number) => void, contentState: Draft.DraftModel.ImmutableData.ContentState) {
        contentBlock.findEntityRanges(
            (character) => {
                const entityKey = character.getEntity();
                return (
                    entityKey !== null &&
                    contentState.getEntity(entityKey).getType() === 'LINK'
                );
            },
            callback
        );
    }

    getLinkPrompt(e: React.MouseEvent) {
        e.preventDefault();
        const selection = this.state.editorState.getSelection();
        const currentContent = this.state.editorState.getCurrentContent();

        const selectedText = this.getSelectedText(this.state.editorState);

        if (!selection.isCollapsed()) {

            const blockWithLinkAtBeginning = currentContent.getBlockForKey(selection.getStartKey());
            const linkKey = blockWithLinkAtBeginning.getEntityAt(selection.getStartOffset());

            const url = linkKey ? currentContent.getEntity(linkKey).getData().url : "";

            this.setState({ linkAddress: url, linkText: selectedText })
        } else {
            this.setState({ linkText: selectedText })
        }
    }

    toggleInlineStyle(e: React.MouseEvent, inlineStyle: string) {
        e.preventDefault();
        this.state.onPropertyChanged(this.toMD(this.state.editorState));
        this.onChange(RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle));
    }

    toggleBlock(e: React.MouseEvent, blockType: string) {
        e.preventDefault();
        this.state.onPropertyChanged(this.toMD(this.state.editorState));
        this.onChange(RichUtils.toggleBlockType(this.state.editorState, blockType));
    }

    toMD(newEditorState: any) {
        const content = newEditorState.getCurrentContent();
        const rawObject = convertToRaw(content);
        rawObject.blocks = rawObject.blocks.map(x => {
            if (x.text.length === 0) {
                // zero width non-joiner
                x.text = String.fromCharCode(8204)
            }
            return x
        })
        const markdownString = draftToMarkdown(rawObject);
        return markdownString;
    }

    handleKeyCommand(command: string, editorState: EditorState) {
        const newState = RichUtils.handleKeyCommand(editorState, command);

        if (newState) {
            this.onChange(newState);
            return 'handled';
        }

        return 'not-handled';
    }

    insertLink(e: React.MouseEvent, url: string) {
        e.preventDefault();
        const selectedText = this.getSelectedText(this.state.editorState);

        const contentStateWithEntity = this.state.editorState.getCurrentContent().createEntity(
            "LINK",
            "MUTABLE",
            { url: url }
        )
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        let contentStateWithUpdatedText = contentStateWithEntity;
        if (selectedText.length > 0) {
            if (selectedText !== this.state.linkText) {
                contentStateWithUpdatedText = Modifier.replaceText(contentStateWithUpdatedText, this.state.editorState.getSelection(), this.state.linkText || this.state.linkAddress, undefined, entityKey)
            }
        } else {
            let selection = this.state.editorState.getSelection();
            if (!selection.isCollapsed()) {
                const contentLength = this.state.editorState.getCurrentContent().getLastBlock().getText().length;
                selection = this.state.editorState.getCurrentContent().getSelectionAfter().merge({ focusOffset: contentLength, anchorOffset: contentLength });
            }
            contentStateWithUpdatedText = Modifier.insertText(contentStateWithUpdatedText, selection, this.state.linkText || this.state.linkAddress, undefined, entityKey)
        }
        const newEditorState = EditorState.set(this.state.editorState, {
            currentContent: contentStateWithUpdatedText
        });

        let newSelection = this.state.editorState.getSelection();
        if (!(selectedText.length === this.state.linkText.length && selectedText.length > 0)) {
            const focusOffset = newEditorState.getSelection().getFocusOffset();
            newSelection = this.state.editorState.getSelection().merge({
                anchorOffset: focusOffset + (this.state.linkText.length || this.state.linkAddress.length)
            })
        }

        this.onChange(RichUtils.toggleLink(newEditorState, newSelection, entityKey));
        this.setState({
            isLinkPopoverOpen: false,
            linkAddress: '',
            linkText: '',
            linkAddressErrorMessage: ''
        })
    }

    getSelectedText(editorState: EditorState) {
        const selection = editorState.getSelection();
        const anchorKey = selection.getAnchorKey();
        const currentContentBlock = editorState.getCurrentContent().getBlockForKey(anchorKey);
        const start = selection.getStartOffset();
        const end = selection.getEndOffset();
        return currentContentBlock.getText().slice(start, end);
    }

    cancelAddingLink(e: React.MouseEvent) {
        this.setState({
            isLinkPopoverOpen: false,
            linkAddress: '',
            linkText: '',
            linkAddressErrorMessage: ''
        });
    }

    handleOpenChange(e: any, data: OnOpenChangeData) {
        if (!data.open) {
            this.setState({
                isLinkPopoverOpen: (data.open || false),
                linkAddress: '',
                linkText: '',
                linkAddressErrorMessage: ''
            })
        } else {
            this.setState({ isLinkPopoverOpen: data.open || false });
        }
    }

    handleLinkAddressChange(e: any, data: InputOnChangeData) {
        const regex: RegExp = /^(https:\/\/)([\da-z\.-]+\.[a-z\.]{2,6}|[\d\.]+)([\/:?=&#]{1}[\da-z\.%_+-]+)*[\/\?=]?$/;
        if (regex.test(data.value.toLowerCase())) {
            this.setState({ linkAddressErrorMessage: "", linkAddress: data.value })
        } else {
            this.setState({ linkAddressErrorMessage: "Only secure HTTPS URLs are allowed", linkAddress: data.value })
        }
    }

    getIsLinkTriggerDisabled() {
        return this.state.editorState.getCurrentContent().entries.length > 20
    }

    render() {
        if (this.state.propertyType === "Summary") {
            return (
                <div className="richEditor">
                    <div className="richEditor__toolbar">
                        <Button onMouseDown={(e: React.MouseEvent) => this.toggleInlineStyle(e, "BOLD")} appearance='transparent' icon={<TextBold16Filled />} />
                        <Button onMouseDown={(e: React.MouseEvent) => this.toggleInlineStyle(e, "ITALIC")} appearance='transparent' icon={<TextItalic16Filled />} />
                        <Button onMouseDown={(e: React.MouseEvent) => this.toggleBlock(e, "unordered-list-item")} appearance='transparent' icon={<TextBulletListLtr16Regular />} />
                        <Button onMouseDown={(e: React.MouseEvent) => this.toggleBlock(e, "ordered-list-item")} appearance='transparent' icon={<TextNumberListLtr16Regular />} />
                        <Popover trapFocus open={this.state.isLinkPopoverOpen} onOpenChange={this.handleOpenChange}>
                            <PopoverTrigger disableButtonEnhancement>
                                <Tooltip content="Summary can contain maximum 20 in-line links" relationship="label">
                                    <Button onClick={this.getLinkPrompt} appearance='transparent' icon={<Link16Regular />} disabled={this.getIsLinkTriggerDisabled()} />
                                </Tooltip>
                            </PopoverTrigger>
                            <PopoverSurface aria-labelledby='linkPopover'>
                                <div className='richEditor__popover'>
                                    <h4>Insert link</h4>
                                    <Field label="Text to display">
                                        <Input
                                            className="inputField"
                                            value={this.state.linkText}
                                            onChange={(e, data) => this.setState({ linkText: data.value })}
                                            autoComplete="off"
                                        />
                                    </Field>

                                    <Field
                                        label="Address"
                                        required
                                        validationState={this.state.linkAddressErrorMessage ? 'error' : 'none'}
                                        validationMessage={this.state.linkAddressErrorMessage}>
                                        <Input
                                            className="inputField"
                                            value={this.state.linkAddress}
                                            onChange={this.handleLinkAddressChange}
                                            autoComplete="off"
                                            required
                                        />
                                    </Field>

                                    <div className='richEditor__buttonContainer'>
                                        <Button onClick={this.cancelAddingLink}>Cancel</Button>
                                        <Button onClick={(e) => this.insertLink(e, this.state.linkAddress)} disabled={!this.state.linkAddress || !!this.state.linkAddressErrorMessage} appearance='primary'>Insert</Button>
                                    </div>
                                </div>
                            </PopoverSurface>
                        </Popover>
                    </div>
                    <div className="richEditor__inputArea">
                        <Editor
                            editorState={this.state.editorState}
                            handleKeyCommand={this.handleKeyCommand}
                            onChange={this.onChange}
                            spellCheck
                        />
                    </div>
                </div>
            );
        } else if (this.state.propertyType === "Author") {
            return (
                <div className="richEditor">
                    <div className="richEditor__toolbar">
                        <Button onClick={(e: React.MouseEvent) => this.toggleInlineStyle(e, "BOLD")} appearance='transparent' icon={<TextBold16Filled />} />
                        <Button onClick={(e: React.MouseEvent) => this.toggleInlineStyle(e, "ITALIC")} appearance='transparent' icon={<TextItalic16Filled />} />
                    </div>
                    <div className="richEditor__inputArea-author">
                        <Editor
                            editorState={this.state.editorState}
                            handleKeyCommand={this.handleKeyCommand}
                            onChange={this.onChange}
                        />
                    </div>
                </div>
            );
        }
    }
}


export default MyEditor